/**
 * New Year garlands — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearGarlandsPack: ToilePack = {
    slug: 'new-year-garlands',
    category: 'season',
    name: 'New Year garlands',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'stars', label: 'Star garland', subject: 'a sheet of paper stars in two sizes, one star lettered "2026", hole marks to thread on a string, dashed cut lines'},
        {key: 'clocks', label: 'Clock garland', subject: 'a sheet of small paper clock faces to cut and string, each at midnight, one clock lettered "12" at the top, dashed cut lines'},
        {key: 'glasses', label: 'Glass chain', subject: 'a sheet of paper champagne glasses to cut and string, one glass lettered "CHEERS", dashed cut lines'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a landscape sheet with a gold border and an even margin, the pieces arranged in a row, print-ready.'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a landscape sheet with a confetti border and a white margin, the pieces arranged in a row, print-ready.'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a landscape sheet with a wide margin, the pieces arranged in a row, print-ready.'},
    ],
    directions: ['for a birthday instead', 'spelling out the year', 'sized for a mantel'],
    directionHint: 'try: for a birthday instead',
    seo: {
        title: 'New Year Garlands: Stars, Clocks & Glass Chain',
        metaDescription: 'Nine New Year paper garlands — a star garland, a clock garland, and a champagne-glass chain — cut lines drawn, in three festive styles.',
        h1: 'New Year garlands: stars, clocks, and glass chain',
        tagline: 'Nine sheets, three garland designs, three styles each — cut lines drawn, ready to string.',
        intro: "Three New Year paper garlands — a string of stars marked with the year, a chain of midnight clock faces, and a champagne-glass chain lettered 'CHEERS' — each with dashed cut lines, in black-and-gold, confetti-party, and minimal styles. Ask for a birthday version or a mantel-sized set, and the same garland grammar carries it.",
        useCases: [
            "a New Year's Eve mantel display",
            'a party backdrop garland',
            'a photo-booth decoration',
            'a countdown party craft',
        ],
        faq: [
            {q: 'Do I need to string these myself?', a: 'Yes — the sheet prints flat with hole marks or tabs; cut the pieces and thread them on string or ribbon yourself.'},
            {q: 'What size do these print at?', a: 'Each sheet is a single landscape page; the pieces print at a size ready to cut by hand.'},
            {q: 'Can I get a birthday version?', a: 'Yes — the direction field takes any variation, including a birthday theme.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
